import { useState, useEffect } from 'react';
import { X, MessageCircle, Edit2, Check, Send, Loader2 } from 'lucide-react';
import { api } from '../services/api';

/**
 * Formats a phone string to the wa.me format: +55DDDNUMBER
 * Strips all non-digits, prepends 55 if not present.
 */
function toWaPhone(raw) {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('55') && digits.length >= 12) return `+${digits}`;
  if (digits.length >= 10) return `+55${digits}`;
  return null;
}

/**
 * Interpolates {{variable}} placeholders with lead data.
 */
function interpolate(template, lead, userName) {
  const fmt = (v) => v != null && v !== '' ? String(v) : '';
  return template
    .replace(/\{\{contato_nome\}\}/g, fmt(lead.contato_nome))
    .replace(/\{\{razao_social\}\}/g, fmt(lead.razao_social))
    .replace(/\{\{produto\}\}/g, fmt(lead.produto_nome || 'Totvs Moda'))
    .replace(/\{\{valor_saas\}\}/g, lead.valor_saas
      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lead.valor_saas)
      : '')
    .replace(/\{\{usuario\}\}/g, fmt(userName))
    .replace(/\{\{stage\}\}/g, fmt(lead.stage));
}

export default function WhatsAppModal({ lead, onClose, userName }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editedMsg, setEditedMsg] = useState('');
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // { ok: bool, msg: string }
  const [apiStatus, setApiStatus] = useState(null); // { configured: bool, provider: string|null }

  const waPhone = toWaPhone(phone);

  useEffect(() => {
    const raw = lead?.contato_telefone || lead?.telefone_empresa || '';
    setPhone(raw);
    api.getWhatsAppTemplates().then(data => {
      setTemplates(data);
      const match = data.find(t => t.active && t.stage === lead?.stage) || data.find(t => t.active);
      if (match) {
        setSelected(match);
        setEditedMsg(interpolate(match.message_template, lead, userName));
      }
    }).catch(() => {});
    // ZYN-59: verifica se provider API está configurado
    api.getWhatsAppStatus().then(setApiStatus).catch(() => setApiStatus({ configured: false, provider: null }));
  }, [lead, userName]);

  function handleSelectTemplate(tpl) {
    setSelected(tpl);
    setEditedMsg(interpolate(tpl.message_template, lead, userName));
    setEditing(false);
    setSendStatus(null);
  }

  function handleOpenWa() {
    if (!waPhone) return;
    const url = `https://wa.me/${waPhone.replace('+', '')}?text=${encodeURIComponent(editedMsg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  }

  async function handleSendApi() {
    if (!phone || !editedMsg.trim()) return;
    setSending(true);
    setSendStatus(null);
    try {
      await api.sendWhatsApp({
        lead_id: lead.id,
        template_id: selected?.id || null,
        phone,
        variables: { message: editedMsg },
      });
      setSendStatus({ ok: true, msg: 'Mensagem enviada com sucesso!' });
      setTimeout(onClose, 1500);
    } catch (err) {
      setSendStatus({ ok: false, msg: err.message || 'Erro ao enviar mensagem.' });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-[#25D366]" />
            <span className="font-semibold text-gray-800">Enviar via WhatsApp</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Phone — editável */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Número</p>
            <input
              type="tel"
              value={phone}
              onChange={e => { setPhone(e.target.value); setSendStatus(null); }}
              placeholder="(11) 99999-9999"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366]/40"
            />
            {phone && !waPhone && (
              <p className="text-xs text-red-500 mt-1">Número inválido — informe ao menos 10 dígitos.</p>
            )}
          </div>

          {/* Templates */}
          {templates.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-2">Template</p>
              <div className="space-y-1">
                {templates.filter(t => t.active).map(tpl => (
                  <button
                    key={tpl.id}
                    onClick={() => handleSelectTemplate(tpl)}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                      selected?.id === tpl.id
                        ? 'border-[#25D366] bg-green-50 text-green-800'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <span className="font-medium">{tpl.name}</span>
                    {tpl.stage && (
                      <span className="ml-2 text-xs text-gray-400">({tpl.stage})</span>
                    )}
                    {selected?.id === tpl.id && (
                      <Check size={12} className="inline ml-2 text-[#25D366]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-gray-500">Mensagem</p>
              <button
                onClick={() => setEditing(e => !e)}
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700"
              >
                <Edit2 size={11} />
                {editing ? 'Visualizar' : 'Editar'}
              </button>
            </div>
            {editing ? (
              <textarea
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none h-36 focus:outline-none focus:ring-2 focus:ring-[#25D366]/40"
                value={editedMsg}
                onChange={e => setEditedMsg(e.target.value)}
              />
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap min-h-[5rem]">
                {editedMsg || <span className="text-gray-400">Selecione um template...</span>}
              </div>
            )}
          </div>

          {/* Status de envio */}
          {sendStatus && (
            <p className={`text-sm px-3 py-2 rounded-lg ${sendStatus.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
              {sendStatus.msg}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex flex-col gap-2">
          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
              Cancelar
            </button>
            {/* Botão wa.me — sempre disponível */}
            <button
              onClick={handleOpenWa}
              disabled={!waPhone || !editedMsg.trim()}
              className="flex items-center gap-2 px-4 py-2 border border-[#25D366] text-[#25D366] text-sm font-medium rounded-lg hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <MessageCircle size={15} />
              Abrir WhatsApp
            </button>
            {/* Botão API — só aparece se WHATSAPP_PROVIDER estiver configurado (detectado via endpoint) */}
            <button
              onClick={handleSendApi}
              disabled={!phone || !editedMsg.trim() || sending}
              className="flex items-center gap-2 px-5 py-2 bg-[#25D366] text-white text-sm font-semibold rounded-lg hover:bg-[#1ebe5d] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Enviar
            </button>
          </div>
          <div className="flex items-center justify-end gap-2 mt-1">
            {apiStatus && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                apiStatus.configured
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {apiStatus.configured
                  ? `✓ API ${apiStatus.provider} ativa · respostas logadas automaticamente`
                  : 'API não configurada — use "Abrir WhatsApp"'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
